import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
} from "recharts";
import {
  FileAudio,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Activity,
  Upload,
  RefreshCw,
  Play,
  Pause,
  Key,
} from "lucide-react";
import toast from "react-hot-toast";
import werService from "../services/werService";
import { calculateWER } from "../utils/werCalculator";
import { useAuth } from "../contexts/AuthContext";

const axisColor = "#cbd5f5";
const gridColor = "rgba(148, 163, 184, 0.2)";
const tooltipStyles = {
  backgroundColor: "#0f172a",
  borderRadius: "0.75rem",
  border: "1px solid rgba(255,255,255,0.1)",
  color: "#e2e8f0",
};

const categoryColors = {
  excellent: "#10b981",
  good: "#3b82f6",
  fair: "#f59e0b",
  poor: "#ef4444",
};

const WERDashboard = () => {
  const { user } = useAuth();
  const [werData, setWerData] = useState([]);
  const [fullDataWithAudio, setFullDataWithAudio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiToken, setApiToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentSegment, setCurrentSegment] = useState(0);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [stats, setStats] = useState({
    totalSegments: 0,
    averageWER: "0.00",
    excellent: 0,
    good: 0,
    fair: 0,
    poor: 0,
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        await loadWERData();
      } catch (error) {
        console.error("Initial load error:", error);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (user?.token) {
      setApiToken(user.token);
    }
  }, [user]);

  const getAPIUrl = () => {
    const baseUrl =
      import.meta.env.VITE_API_URL || "https://api.mayorsoftware.ch/v1";
    if (baseUrl.includes("localhost:3001")) {
      return "http://localhost:8000";
    }
    return baseUrl;
  };

  const loadWERData = async () => {
    try {
      setLoading(true);
      const data = await werService.getWERData();

      if (Array.isArray(data) && data.length) {
        setWerData(data);
        calculateStats(data);
        toast.success(`${data.length} segments chargés`);
      } else {
        setWerData([]);
        calculateStats([]);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des données WER:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      let data;

      if (file.name.endsWith(".zip")) {
        data = await werService.parseZipFile(file);
        toast.success(`${data.length} segments chargés depuis le ZIP`);
        setFullDataWithAudio(data);
      } else {
        data = await werService.parseResultsFile(file);
        toast.success(`${data.length} segments chargés`);
        setFullDataWithAudio(data);
      }

      setWerData(data);
      calculateStats(data);
    } catch (error) {
      console.error("Erreur lors de l'analyse du fichier:", error);
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    if (!Array.isArray(data) || !data.length) {
      setStats({
        totalSegments: 0,
        averageWER: "0.00",
        excellent: 0,
        good: 0,
        fair: 0,
        poor: 0,
      });
      return;
    }

    const totalSegments = data.length;
    const averageWER =
      data.reduce((sum, item) => sum + (item.wer || 0), 0) / totalSegments;

    const categories = {
      excellent: data.filter((d) => d.wer < 5).length,
      good: data.filter((d) => d.wer >= 5 && d.wer < 10).length,
      fair: data.filter((d) => d.wer >= 10 && d.wer < 20).length,
      poor: data.filter((d) => d.wer >= 20).length,
    };

    setStats({
      totalSegments,
      averageWER: averageWER.toFixed(2),
      ...categories,
    });
  };

  const transcribeAudio = async (audioFile, token) => {
    const formData = new FormData();
    formData.append("audio", audioFile);
    formData.append("diarization", "false");

    const response = await fetch(`${getAPIUrl()}/v1/transcribe/sync`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      let errorMsg = "Erreur de transcription";
      try {
        const error = await response.json();
        errorMsg = error.detail || JSON.stringify(error);
      } catch {
        errorMsg = `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMsg);
    }

    return response.json();
  };

  const createAudioFile = async (segment) => {
    if (segment.audio_base64) {
      try {
        const binaryString = atob(segment.audio_base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: "audio/wav" });
        return new File([blob], segment.audio_file || `${segment.id}.wav`, {
          type: "audio/wav",
        });
      } catch (error) {
        console.error("Erreur de décodage base64:", error);
        throw new Error("Impossible de décoder l'audio base64");
      }
    }

    throw new Error("Pas de fichier audio disponible (audio_base64 requis)");
  };

  const processWERTesting = async () => {
    if (!apiToken) {
      toast.error("Veuillez entrer votre token API");
      return;
    }

    const dataToProcess = fullDataWithAudio.length
      ? fullDataWithAudio
      : werData;
    if (!dataToProcess.length) {
      toast.error("Aucun segment à traiter. Chargez d'abord un dataset.");
      return;
    }

    setIsProcessing(true);
    setCurrentSegment(0);
    setProcessingProgress(0);

    const results = [];
    let processed = 0;

    for (let i = 0; i < dataToProcess.length; i++) {
      const segment = dataToProcess[i];
      setCurrentSegment(i + 1);

      try {
        const audioFile = await createAudioFile(segment);
        const transcription = await transcribeAudio(audioFile, apiToken);
        const hypothesis = transcription.text || "";
        const werResult = calculateWER(segment.reference_text, hypothesis);

        const updatedSegment = {
          ...segment,
          wer: werResult.wer,
          hypothesis_text: hypothesis,
          reference_length: werResult.refLength,
          hypothesis_length: werResult.hypLength,
          category:
            werResult.wer < 5
              ? "excellent"
              : werResult.wer < 10
                ? "good"
                : werResult.wer < 20
                  ? "fair"
                  : "poor",
          status: "tested",
        };

        results.push(updatedSegment);
        processed += 1;
        setWerData([...results, ...dataToProcess.slice(i + 1)]);
        calculateStats(results);
        setProcessingProgress((processed / dataToProcess.length) * 100);

        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`Erreur sur segment ${segment.segment}:`, error);
        toast.error(`Erreur: ${segment.segment} - ${error.message}`);
        results.push({
          ...segment,
          status: "error",
          error: error.message,
        });
      }
    }

    await werService.saveWERData(results, false);
    setWerData(results);
    calculateStats(results);
    setIsProcessing(false);
    setProcessingProgress(100);
    toast.success(
      `Test WER terminé! ${processed}/${dataToProcess.length} segments traités`,
    );
  };

  const pieData = [
    {
      name: "Excellent (<5%)",
      value: stats.excellent,
      color: categoryColors.excellent,
    },
    { name: "Bon (5-10%)", value: stats.good, color: categoryColors.good },
    { name: "Correct (10-20%)", value: stats.fair, color: categoryColors.fair },
    { name: "Faible (≥20%)", value: stats.poor, color: categoryColors.poor },
  ];

  const distributionData = Array.from({ length: 10 }, (_, i) => {
    const min = i * 5;
    const max = (i + 1) * 5;
    return {
      range: `${min}-${max}%`,
      count: werData.filter((d) => d.wer >= min && d.wer < max).length,
    };
  });

  const heroMetrics = [
    {
      label: "Segments",
      value: stats.totalSegments,
      hint: "Dataset suivi",
      icon: FileAudio,
    },
    {
      label: "WER moyen",
      value: `${stats.averageWER}%`,
      hint: "Qualite cumulee",
      icon: TrendingUp,
    },
    {
      label: "Excellent",
      value: stats.excellent,
      hint: "WER < 5%",
      icon: CheckCircle,
    },
    {
      label: "Alertes",
      value: stats.poor,
      hint: "WER >= 20%",
      icon: AlertCircle,
    },
  ];

  const glassPanelClass =
    "rounded-3xl border border-white/10 bg-white/5 backdrop-blur-2xl shadow-[0_35px_120px_rgba(15,23,42,0.6)] p-6";
  const glassInsetClass =
    "rounded-2xl border border-white/10 bg-white/5 backdrop-blur-2xl";
  const glassInputClass =
    "w-full rounded-2xl border border-white/15 bg-slate-900/70 px-4 py-3 text-sm text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-cyan-300";
  const badgeClass =
    "inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1 text-[0.65rem] uppercase tracking-[0.4em] text-cyan-100/70";

  if (loading) {
    return (
      <div className="flex h-[70vh] w-full items-center justify-center bg-[#030816] text-white">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-2 border-white/30 border-t-cyan-300" />
          <p className="text-sm text-white/60">Chargement des mesures WER…</p>
        </div>
      </div>
    );
  }

  const hasData = werData.length > 0;

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#030816] text-white">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute -top-32 left-1/4 h-[28rem] w-[28rem] rounded-full bg-cyan-500/25 blur-[160px]" />
        <div className="absolute bottom-0 right-0 h-[26rem] w-[26rem] rounded-full bg-indigo-500/20 blur-[140px]" />
        <div className="absolute inset-0 opacity-20">
          <div className="h-full w-full [background:radial-gradient(circle_at_1px,_rgba(255,255,255,0.12)_1px,_transparent_0)] [background-size:34px_34px]" />
        </div>
      </div>

      <div className="relative z-10 px-4 pt-6 pb-12 sm:px-8 lg:px-12 space-y-8">
        <section className={`${glassPanelClass} space-y-6`}>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="mt-4 text-3xl font-semibold text-white">
                Observatoire du Word Error Rate
              </h1>
              <p className="mt-2 text-sm text-white/70 max-w-3xl">
                Importez des transcriptions, rejouez des batches sur la stack
                Whisper de production et capturez une telemetrie qualite
                detaillee pour chaque segment.
              </p>
            </div>
            <div className="rounded-3xl border border-white/10 bg-slate-950/50 px-6 py-4 text-right">
              <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/60">
                Statut live
              </p>
              <p className="mt-3 text-3xl font-semibold text-white">
                {stats.averageWER}%
              </p>
              <p className="text-xs text-white/60">WER moyen sur le dataset</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {heroMetrics.map((metric) => {
              const Icon = metric.icon;
              return (
                <div
                  key={metric.label}
                  className="rounded-2xl border border-white/10 bg-slate-900/60 p-4 backdrop-blur-xl"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.35em] text-white/50">
                        {metric.label}
                      </p>
                      <p className="mt-2 text-2xl font-semibold">
                        {metric.value}
                      </p>
                      <p className="text-xs text-white/60">{metric.hint}</p>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                      <Icon className="h-5 w-5 text-cyan-200" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <div className={`${glassPanelClass} space-y-4`}>
            <div className="flex items-center gap-2">
              <Key className="h-4 w-4 text-cyan-200" />
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-cyan-200/70">
                  Token
                </p>
                <h3 className="mt-1 text-xl font-semibold">Authentification</h3>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs text-white/60">Token API</label>
              <div className="flex gap-3">
                <input
                  type={showToken ? "text" : "password"}
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  placeholder="epv_..."
                  className={glassInputClass}
                />
                <button
                  type="button"
                  onClick={() => setShowToken((prev) => !prev)}
                  className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/70"
                >
                  {showToken ? "Masquer" : "Afficher"}
                </button>
              </div>
              <p className="text-xs text-white/50">
                Les tokens sont synchronises depuis votre session. Collez une
                cle alternative pour tester des scopes differents.
              </p>
            </div>
          </div>

          <div className={`${glassPanelClass} space-y-4`}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-cyan-200/70">
                  Dataset
                </p>
                <h3 className="mt-2 text-xl font-semibold">
                  Chargement et actualisation
                </h3>
              </div>
              <span className="rounded-full border border-white/10 bg-white/5 px-4 py-1 text-xs text-white/70">
                {werData.length ? `${werData.length} segments` : "Vide"}
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              <label className="inline-flex flex-1 min-w-[180px] items-center justify-center gap-2 rounded-2xl border border-white/15 bg-gradient-to-r from-cyan-500/30 to-blue-500/30 px-4 py-3 text-sm font-semibold text-white transition hover:from-cyan-400/40 hover:to-blue-400/40 cursor-pointer">
                <Upload className="h-4 w-4" />
                Importer un fichier
                <input
                  type="file"
                  accept=".txt,.json,.zip"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
              <button
                type="button"
                onClick={loadWERData}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/80"
              >
                <RefreshCw className="h-4 w-4" />
                Actualiser
              </button>
            </div>
            <p className="text-xs text-white/50">
              ZIP (audio + texte), JSON (dataset) ou TXT (résultats WER)
              acceptés.
            </p>
          </div>

          <div className={`${glassPanelClass} space-y-4`}>
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-cyan-200/70">
                Pipeline
              </p>
              <h3 className="mt-2 text-xl font-semibold">Statut d'execution</h3>
            </div>
            <div className={`${glassInsetClass} border-white/5 px-4 py-3`}>
              <div className="text-sm text-white/70">
                Segment {Math.min(currentSegment, werData.length)} /{" "}
                {werData.length || 0}
              </div>
              <div className="mt-3 h-3 rounded-full bg-white/10">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all"
                  style={{ width: `${processingProgress}%` }}
                />
              </div>
              <div className="mt-2 text-xs text-white/50">
                {processingProgress.toFixed(1)}% complété
              </div>
            </div>
            <button
              type="button"
              onClick={processWERTesting}
              disabled={isProcessing || !apiToken || !werData.length}
              className={`w-full rounded-2xl px-4 py-3 text-sm font-semibold text-white transition ${
                isProcessing || !apiToken || !werData.length
                  ? "bg-white/10 text-white/40 cursor-not-allowed"
                  : "bg-gradient-to-r from-emerald-400 to-cyan-500 hover:from-emerald-300 hover:to-cyan-400"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                {isProcessing ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                {isProcessing ? "Traitement en cours…" : "Lancer le test WER"}
              </span>
            </button>
            {!apiToken && (
              <p className="text-xs text-rose-300">Token API requis</p>
            )}
          </div>
        </section>

        {!hasData && (
          <section className={`${glassPanelClass} text-center space-y-4`}>
            <FileAudio className="mx-auto h-16 w-16 text-white/30" />
            <h2 className="text-2xl font-semibold text-white">
              Aucune donnée WER chargée
            </h2>
            <p className="text-sm text-white/60 max-w-2xl mx-auto">
              Importez un dataset pour commencer à analyser la précision de vos
              transcriptions Whisper Large-v3-Turbo. Les ZIP peuvent inclure les
              audio_base64 pour exécuter des replays automatiques.
            </p>
          </section>
        )}

        {hasData && (
          <>
            <section className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              {["totalSegments", "averageWER", "excellent", "poor"].map(
                (key) => {
                  const config = {
                    totalSegments: {
                      label: "Total segments",
                      value: stats.totalSegments,
                      accent: "from-indigo-500/40 to-purple-500/30",
                      icon: FileAudio,
                      subtitle: "Clips suivis",
                    },
                    averageWER: {
                      label: "WER moyen",
                      value: `${stats.averageWER}%`,
                      accent: "from-cyan-500/40 to-teal-500/30",
                      icon: Activity,
                      subtitle: "Qualite globale",
                    },
                    excellent: {
                      label: "Excellent",
                      value: stats.excellent,
                      accent: "from-emerald-500/40 to-lime-500/30",
                      icon: CheckCircle,
                      subtitle: "< 5%",
                    },
                    poor: {
                      label: "À améliorer",
                      value: stats.poor,
                      accent: "from-rose-500/40 to-orange-500/30",
                      icon: AlertCircle,
                      subtitle: ">= 20%",
                    },
                  }[key];

                  const Icon = config.icon;
                  return (
                    <div
                      key={key}
                      className={`${glassPanelClass} bg-gradient-to-br ${config.accent}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs uppercase tracking-[0.3em] text-white/70">
                            {config.label}
                          </p>
                          <p className="mt-2 text-3xl font-semibold">
                            {config.value}
                          </p>
                          <p className="text-xs text-white/70">
                            {config.subtitle}
                          </p>
                        </div>
                        <div className="rounded-3xl border border-white/30 bg-white/10 p-3">
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                      </div>
                    </div>
                  );
                },
              )}
            </section>

            <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className={`${glassPanelClass}`}>
                <h2 className="text-xl font-semibold">
                  Distribution par catégorie
                </h2>
                <p className="text-sm text-white/60">
                  Répartition des segments selon leurs performances
                </p>
                <div className="mt-6 h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100 || 0).toFixed(0)}%`
                        }
                        outerRadius={120}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyles} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className={`${glassPanelClass}`}>
                <h2 className="text-xl font-semibold">
                  Distribution par tranches
                </h2>
                <p className="text-sm text-white/60">
                  Volumes cumulés par intervalles de WER
                </p>
                <div className="mt-6 h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={distributionData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                      <XAxis
                        dataKey="range"
                        stroke={axisColor}
                        style={{ fontSize: "12px" }}
                      />
                      <YAxis stroke={axisColor} style={{ fontSize: "12px" }} />
                      <Tooltip contentStyle={tooltipStyles} />
                      <Bar
                        dataKey="count"
                        fill="#3b82f6"
                        radius={[10, 10, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </section>

            <section className={`${glassPanelClass}`}>
              <div className="flex flex-col gap-2">
                <h2 className="text-xl font-semibold">WER par segment</h2>
                <p className="text-sm text-white/60">
                  Suivi chronologique des scores
                </p>
              </div>
              <div className="mt-6 h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={werData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis
                      dataKey="segment"
                      stroke={axisColor}
                      style={{ fontSize: "10px" }}
                    />
                    <YAxis
                      stroke={axisColor}
                      style={{ fontSize: "12px" }}
                      label={{
                        value: "WER (%)",
                        angle: -90,
                        position: "insideLeft",
                        fill: axisColor,
                      }}
                    />
                    <Tooltip contentStyle={tooltipStyles} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="wer"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      dot={{ r: 2 }}
                      name="WER (%)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className={`${glassPanelClass}`}>
              <div className="flex flex-col gap-2">
                <h2 className="text-xl font-semibold">
                  WER vs longueur de référence
                </h2>
                <p className="text-sm text-white/60">
                  Détectez les dérives selon la taille des segments
                </p>
              </div>
              <div className="mt-6 h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis
                      type="number"
                      dataKey="reference_length"
                      name="Longueur"
                      stroke={axisColor}
                      label={{
                        value: "Mots de référence",
                        position: "insideBottom",
                        fill: axisColor,
                        offset: -5,
                      }}
                    />
                    <YAxis
                      type="number"
                      dataKey="wer"
                      name="WER"
                      stroke={axisColor}
                      label={{
                        value: "WER (%)",
                        angle: -90,
                        position: "insideLeft",
                        fill: axisColor,
                      }}
                    />
                    <Tooltip
                      contentStyle={tooltipStyles}
                      cursor={{ strokeDasharray: "3 3" }}
                    />
                    <Legend />
                    <Scatter
                      name="Segments"
                      data={werData}
                      fill="#06b6d4"
                      fillOpacity={0.7}
                    />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className={`${glassPanelClass}`}>
              <div className="flex flex-col gap-2">
                <h2 className="text-xl font-semibold">Détails par segment</h2>
                <p className="text-sm text-white/60">
                  Top 20 des segments pour revue rapide
                </p>
              </div>
              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-[0.2em] text-white/60">
                    <tr>
                      <th className="pb-3 text-left">Segment</th>
                      <th className="pb-3 text-left">WER</th>
                      <th className="pb-3 text-left">Catégorie</th>
                      <th className="pb-3 text-left">Réf.</th>
                      <th className="pb-3 text-left">Hyp.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {werData.slice(0, 20).map((item) => (
                      <tr
                        key={item.segment}
                        className="border-t border-white/10"
                      >
                        <td className="py-3 font-mono text-xs text-white/80">
                          {item.segment}
                        </td>
                        <td className="py-3">
                          <span
                            className="rounded-full px-3 py-1 text-xs font-semibold"
                            style={{
                              backgroundColor: `${
                                categoryColors[item.category] || "#1e293b"
                              }33`,
                              color: categoryColors[item.category] || "#e2e8f0",
                            }}
                          >
                            {item.wer}%
                          </span>
                        </td>
                        <td className="py-3 capitalize text-white/70">
                          {item.category === "excellent"
                            ? "Excellent"
                            : item.category === "good"
                              ? "Bon"
                              : item.category === "fair"
                                ? "Correct"
                                : item.category === "poor"
                                  ? "Faible"
                                  : item.category}
                        </td>
                        <td className="py-3 text-white/70">
                          {item.reference_length}
                        </td>
                        <td className="py-3 text-white/70">
                          {item.hypothesis_length}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {werData.length > 20 && (
                  <p className="mt-4 text-center text-xs text-white/50">
                    Affichage de 20 segments sur {werData.length}
                  </p>
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default WERDashboard;
